import { createSelector } from "reselect"
import { get, chain, includes } from "lodash"
import { ERROR } from "helpers/data-statuses"
import { makeCurrencyFormatter, numberFormatter, percentageFormatter } from "helpers/formatters/numbers"
import { getOptimizationStatus } from "modules/smart-trade/selectors"
import { treatAsFactor } from "helpers/securities"
import { Creators as ProfileCreators } from "model/profileModal"
import { classificationsMap } from "helpers/classification"
import { getIdTickerAndMicFromItem, formatItemLabel } from "./utils"

const EMPTY_ARRAY = []

export const getPositionAction = (item) => {
  if (treatAsFactor(item.assetClass)) {
    return ProfileCreators.openFactorProfile(item.id)
  }
  if (!includes(["equity", "swap"], item.assetClass)) {
    return null
  }
  const { id, ticker, mic } = getIdTickerAndMicFromItem(item)
  // Altough item.id and id will most likely be the same, we are simply checking for existence here so we want to look
  // at the value before it is being processed
  if (!ticker && !mic && !item.id) {
    return null
  }
  return ProfileCreators.openSecurityProfile2(item.assetClass, id, ticker, mic)
}

export const GROUP_OPTIONS = [{
  id: "topBySize",
  value: "topBySize",
  label: "Top 10 By Position Size",
}, {
  id: "topByADV",
  value: "topByADV",
  label: "Top 10 By % ADV",
}, {
  id: "topFactors",
  value: "topFactors",
  label: "Top 10 Factors",
}]

export const getSelectedDate = (_, props) => get(props, "selectedDate")
export const getReadOnly = (_, props) => get(props, "readOnly", false)
export const getRawSelectedPositionSet = (state) => get(state, "global.data.selectedExperimentPositionSet.data.researchTopic.experiment.dates")
export const getSelectedGroup = state => get(state, "modules.smartTradePanel.selectedGroup")
export const getSmartTradeMeta = state => get(state, "global.data.smartTradeExperimentMeta.data")

const getPropsSmartTradePositionSet = (_, props) => get(props, "smartTradePositionSet")

const getRawClassifications = (state) => get(state, "global.data.classifications.data.classifications")
const getRawSelectedPanelTab = state => get(state, "modules.smartTradePanel.selectedPanelTab")
const getBasketDataStatus = state => get(state, "global.data.smartTradePanelBasket.status")
const getRawBasketComposition = state => get(state, "global.data.smartTradePanelBasket.data.model.simulation.basketSummary.0")

const getSmartTradeDataStatus = state => get(state, "global.data.smartPortfolioPositions.status")
const getRawFactorExposure = state => get(state, "global.data.smartPortfolioPositions.data.model.simulation.exposure.0")
const getRawDollarCompositionSmartTrade = state => get(state, "global.data.smartPortfolioPositions.data.model.simulation.dollarComposition.0")
const getRawSmartPortfolioPositions = (state) => get(state, "global.data.smartPortfolioPositions.data.model.simulation.composition.0.positions", EMPTY_ARRAY)

export const getStatus = createSelector(
  [getBasketDataStatus, getSmartTradeDataStatus, getOptimizationStatus, getReadOnly],
  (basketStatus, smartTradeStatus, optimizationStatus, readOnly) => readOnly ? [basketStatus, smartTradeStatus] : [basketStatus, smartTradeStatus, optimizationStatus],
)

export const getTabsEnabled = createSelector(
  [getStatus],
  (status) => !includes(status, ERROR) ? ["summary", "positions", "rules"] : ["rules"],
)

export const getSelectedPanelTab = createSelector(
  [getRawSelectedPanelTab, getTabsEnabled],
  (selectedTab, tabsEnabled) => includes(tabsEnabled, selectedTab) ? selectedTab : "rules",
)

export const getWorkingEquity = createSelector(
  [getRawDollarCompositionSmartTrade],
  (composition) => composition ? composition.referenceEquity || composition.gmv : null,
)

export const getPositionsDollars = createSelector(
  [getRawDollarCompositionSmartTrade],
  (dollarComposition) => chain(get(dollarComposition, "positions", [])).keyBy("id").mapValues(({ economicExposure }) => economicExposure).value(),
)

export const getBasketSummaryData = createSelector(
  [getRawBasketComposition, getPropsSmartTradePositionSet],
  (basketComposition, smartTradePositionSet) => smartTradePositionSet ? [{ label: "Positions", value: get(basketComposition, "positionsCount", "-") },
    { label: "Market Value", value: makeCurrencyFormatter()(get(basketComposition, "gmv", 0)) },
    { label: "Concentration", value: `${numberFormatter(get(basketComposition, "concentration.min", 0))} - ${percentageFormatter(get(basketComposition, "concentration.max", 0), true)}` },
    { label: "Weighted Market Cap", value: makeCurrencyFormatter()(get(basketComposition, "summaryStats.average.marketCapitalization")) }] : null,
)

export const getSmartTradeSummaryData = createSelector(
  [getSelectedGroup, getRawSmartPortfolioPositions, getRawDollarCompositionSmartTrade, getRawFactorExposure],
  (selectedGroup, percentComposition, dollarComposition, exposure) => {
    if (!percentComposition || !dollarComposition || !exposure) {
      return []
    }
    let orderedItems
    let takeCount = 10

    switch (selectedGroup) {
      case "topFactors":
        orderedItems = chain(exposure.factors).map(elem => ({
          label: elem.name,
          value: elem.net,
          raw: true,
        }))
        break
      case "topByADV":
        orderedItems = chain(dollarComposition.positions).map(elem => ({
          label: formatItemLabel(elem),
          value: elem.averageDailyVolume === 0 ? 0 : elem.economicExposure / elem.averageDailyVolume * 100,
          action: getPositionAction(elem),
        }))
        break
      default:
        orderedItems = chain(percentComposition).map(elem => ({
          label: formatItemLabel(elem),
          value: elem.economicExposure,
          action: getPositionAction(elem),
        }))
    }

    orderedItems = orderedItems.orderBy(elem => Math.abs(elem.value), "desc").value()

    if (orderedItems.length > 10 && selectedGroup !== "topFactors") {
      const top10Value = chain(orderedItems).take(takeCount).sumBy(item => Math.abs(item.value)).value()
      orderedItems.unshift({ label: "Top 10 positions", value: top10Value })
      takeCount++
    }

    return chain(orderedItems).take(takeCount).map(elem => ({
      ...elem,
      chartWidth: selectedGroup === "topFactors" ? 0 : percentageFormatter(Math.abs(elem.value), true),
    })).value()
  },
)

export const getClassificationsTiers = createSelector(
  [getRawClassifications, getSelectedDate],
  (classifications, selectedDate) => {
    return classificationsMap(classifications, selectedDate)
  },
)
